import { InvoiceCard } from "@/components/shared/invoices/invoice-card";
import { useScrollRefresh } from "@/components/shared/scroll-content/scroll-refresh-context";
import { Button, View } from "@/components/ui";
import { InvoiceCardSkeleton } from "./skeleton";
import { useInvoicesByFiltersQuery } from "@/gqlhooks/invoice/useInvoicesByFiltersQuery";
import type { InvoiceFilterStatusEnum } from "@/types/invoice";
import { PaymentMethodScreenModal } from "@/types/invoice";
import { getInvoiceFilters } from "../lib";
import { useAuthState } from "@/store/auth";
import { useClientStore } from "@/store/auth/client";
import { useEffect } from "react";
import { useRoute } from "@/components/shared/router";
import type {
  InvoiceSourceTypeEnum,
  PackageDepartmentEnum,
} from "@/__generated__/graphql";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { useInvoiceState } from "@/store/invoices";
import { getPlatform } from "@/lib/utils";
import { PaymentMethodContainer } from "../payment-method";
import NoInvoices from "@/assets/images/3d-illus/no-invoices.png";
import { EmptyCard } from "@/components/shared/empty-card";

export function InvoiceList({ type }: { type: InvoiceFilterStatusEnum }) {
  const { refreshing, setRefreshing } = useScrollRefresh();
  const { isMobile } = useWindowDimensions();
  const { push } = useRoute();
  const clientId = useAuthState((state) => state.data.userInfo.id);
  const user = useAuthState((state) => state.data.userInfo);

  const { refetch: clientRefetch } = useClientStore();

  const {
    list: invoices,
    loading,
    refetch,
    loadMore,
    canLoadMore,
  } = useInvoicesByFiltersQuery(getInvoiceFilters(clientId, type));

  const {
    setInvoiceId,
    showPaymentMethodModal,
    setInvoiceDepartments,
    setInvoiceSourceType,
  } = useInvoiceState();

  useEffect(() => {
    if (refreshing) {
      refetch();
      if (user.id) clientRefetch({ requestPayload: { id: user.id } });
      setRefreshing(false);
    }
  }, [refreshing, refetch, setRefreshing]);

  const handleViewInvoice = (invoiceId: string) => () => {
    push({
      pageKey: "invoiceDetail",
      params: {
        id: invoiceId,
      },
    });
  };

  const handlePay =
    (
      invoiceId: string,
      departments: PackageDepartmentEnum[],
      sourceType: InvoiceSourceTypeEnum,
    ) =>
    () => {
      setInvoiceId(invoiceId);
      setInvoiceDepartments(departments);
      setInvoiceSourceType(sourceType);
      showPaymentMethodModal(PaymentMethodScreenModal.SELECT_PAYMENT);
      if (isMobile) {
        push({ pageKey: "selectPayment" });
      }
    };

  const platform = getPlatform();

  return (
    <>
      <View className="mt-4 flex flex-col gap-4 px-4 pb-8 md:px-0">
        {invoices?.length ? (
          <View className="flex flex-col gap-4">
            {invoices.map((invoice, index) => (
              <InvoiceCard
                onPay={handlePay(
                  invoice.id,
                  invoice.department,
                  invoice.sourceType,
                )}
                key={index}
                {...invoice}
                onClick={handleViewInvoice(invoice.id)}
              />
            ))}
          </View>
        ) : loading ? (
          <View className="flex flex-col gap-4">
            <InvoiceCardSkeleton />
            <InvoiceCardSkeleton />
            <InvoiceCardSkeleton />
          </View>
        ) : (
          <EmptyCard
            src={NoInvoices}
            title="No invoices yet!"
            desc="We'll send your invoice at month's end for recurring bookings and the next day for one-time ones."
          />
        )}
        {canLoadMore && (
          <Button
            variant="tertiary"
            color="CTA2"
            fullWidth="full"
            loading={loading}
            onClick={loadMore}
            children="Load more"
          />
        )}
      </View>
      {platform === "web" && <PaymentMethodContainer />}
    </>
  );
}
